<template>
	<!--
		Delphi B311 — aba "Calc Peso Var" (layout conforme print do coletor):
		- Peso (+ botão B) e QtdCaixa empilhados
		- Totalizadores: lixeira + Kg | Cx
		- Grid: Seq | Peso | Qtd Cx | DT_HR_BIPAGEM_133
		- Rodapé: Limpar Calc | Gerar Lanç
	-->
	<div class="calc-peso-var flex flex-column h-full w-full p-2 gap-2">
		<!-- Entrada: Peso + B / QtdCaixa (empilhados, como no Delphi) -->
		<div class="calc-peso-var__inputs flex flex-column align-items-center gap-2 py-1">
			<div class="calc-peso-var__row flex flex-row align-items-center gap-2">
				<label class="calc-peso-var__label" for="calc-peso-var-peso">Peso</label>
				<input
					id="calc-peso-var-peso"
					ref="pesoInputRef"
					v-model="pesoStr"
					type="text"
					inputmode="decimal"
					class="calc-peso-var__field"
					autocomplete="off"
					@keydown.enter.prevent="focarQtd"
				>
				<button
					type="button"
					class="calc-peso-var__btn-b"
					title="Registrar bipagem (B)"
					@click="registrarBipagem"
				>
					B
				</button>
			</div>

			<div class="calc-peso-var__row flex flex-row align-items-center gap-2">
				<label class="calc-peso-var__label" for="calc-peso-var-qtd">QtdCaixa</label>
				<input
					id="calc-peso-var-qtd"
					ref="qtdInputRef"
					v-model="qtdCxStr"
					type="text"
					inputmode="numeric"
					class="calc-peso-var__field"
					autocomplete="off"
					@keydown.enter.prevent="registrarBipagem"
				>
			</div>
		</div>

		<!-- Totalizadores (Delphi: lixeira à esquerda + 0,00 Kg | 0 Cx) -->
		<div class="calc-peso-var__totais-wrap">
			<div class="calc-peso-var__totais-title">Totalizadores</div>
			<div class="calc-peso-var__totais flex flex-row align-items-center gap-3">
				<button
					type="button"
					class="calc-peso-var__btn-trash"
					title="Limpar bipagens"
					:disabled="bipagens.length === 0"
					@click="limparBipagens"
				>
					<span class="pi pi-trash" />
				</button>
				<span class="calc-peso-var__total-item">
					{{ formatKg(totalKg) }} Kg
				</span>
				<span class="calc-peso-var__sep">|</span>
				<span class="calc-peso-var__total-item">
					{{ totalCx }} Cx
				</span>
			</div>
		</div>

		<!-- Grade de bipagens -->
		<div class="calc-peso-var__grid flex-grow-1 overflow-auto surface-border border-1 min-h-0">
			<DataTable
				:value="bipagens"
				data-key="seq"
				striped-rows
				scrollable
				scroll-height="flex"
				size="small"
				class="p-datatable-sm text-sm calc-peso-var__table"
				:empty-message="emptyMessage"
			>
				<Column field="seq" header="Seq" style="min-width: 3rem; width: 3.5rem" />
				<Column field="peso" header="Peso" style="min-width: 5rem">
					<template #body="{ data }">
						{{ formatKg(data.peso) }}
					</template>
				</Column>
				<Column field="qtdCx" header="Qtd Cx" style="min-width: 4.5rem" />
				<Column field="dtHrBipagem" header="DT_HR_BIPAGEM_133" style="min-width: 10rem">
					<template #body="{ data }">
						{{ formatDateTime(data.dtHrBipagem) }}
					</template>
				</Column>
			</DataTable>
		</div>

		<!-- Rodapé Delphi: Limpar Calc (esq) · Gerar Lanç (dir) -->
		<div class="calc-peso-var__footer flex flex-row justify-content-between align-items-center gap-2 flex-wrap">
			<R3Button
				label="Limpar Calc"
				icon="pi pi-times"
				severity="danger"
				class="p-button-sm"
				:disabled="bipagens.length === 0 && !pesoStr && !qtdCxStr"
				@click="limparCalc"
			/>
			<R3Button
				label="Gerar Lanç"
				icon="pi pi-check"
				severity="success"
				class="p-button-sm"
				:disabled="bipagens.length === 0"
				@click="gerarLancamento"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import type { FPROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR } from "~/classes/PROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR/FPROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR";

	type BipagemPesoVar = {
		seq: number;
		peso: number;
		qtdCx: number;
		dtHrBipagem: Date;
	};

	const props = defineProps<{
		parent: FPROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR;
	}>();

	const { msgDlg } = useMessageDlg();
	const store = useConferenciaPlanilhaCegaStore();

	const emptyMessage = "<Não há dados para mostrar>";

	const pesoStr = ref("");
	const qtdCxStr = ref("");
	const bipagens = ref<BipagemPesoVar[]>([]);
	const proxSeq = ref(1);

	const pesoInputRef = ref<HTMLInputElement | null>(null);
	const qtdInputRef = ref<HTMLInputElement | null>(null);

	const totalKg = computed(() =>
		bipagens.value.reduce((acc, b) => acc + (Number(b.peso) || 0), 0),
	);
	const totalCx = computed(() =>
		bipagens.value.reduce((acc, b) => acc + (Number(b.qtdCx) || 0), 0),
	);

	function parseNumBR(s: string): number {
		const t = String(s ?? "").trim().replace(/\s/g, "").replace(",", ".");
		if (!t) return NaN;
		const n = Number(t);
		return Number.isFinite(n) ? n : NaN;
	}

	function formatKg(v: number): string {
		const n = Number(v) || 0;
		return n.toLocaleString("pt-BR", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 3,
		});
	}

	function formatDateTime(d: Date): string {
		try {
			return d.toLocaleString("pt-BR", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
			});
		}
		catch {
			return "";
		}
	}

	function focarQtd() {
		qtdInputRef.value?.focus();
		qtdInputRef.value?.select();
	}

	function focarPeso() {
		pesoInputRef.value?.focus();
		pesoInputRef.value?.select();
	}

	/**
	 * Botão B / Enter em QtdCaixa — registra bipagem na grade (DT_HR_BIPAGEM_133).
	 * Sessão local (tabela 133 não está no snapshot Delphi desta rotina).
	 */
	async function registrarBipagem() {
		const peso = parseNumBR(pesoStr.value);
		const qtdCx = parseNumBR(qtdCxStr.value);

		if (!Number.isFinite(peso) || peso <= 0) {
			await msgDlg("Calc Peso Var", "atencao", "Informe o peso (maior que zero).", ["OK"], false);
			focarPeso();
			return;
		}
		if (!Number.isFinite(qtdCx) || qtdCx <= 0) {
			await msgDlg("Calc Peso Var", "atencao", "Informe a quantidade de caixas.", ["OK"], false);
			focarQtd();
			return;
		}

		bipagens.value = [
			...bipagens.value,
			{
				seq: proxSeq.value,
				peso,
				qtdCx: Math.trunc(qtdCx),
				dtHrBipagem: new Date(),
			},
		];
		proxSeq.value += 1;

		// Próxima bipagem: limpa peso, mantém qtd caixas (fluxo de balança)
		pesoStr.value = "";
		await nextTick();
		focarPeso();
	}

	async function limparBipagens() {
		if (bipagens.value.length === 0) return;
		const r = await msgDlg(
			"Calc Peso Var",
			"atencao",
			"Limpar todas as bipagens?",
			["Não", "Sim"],
			false,
		);
		if (r !== 1) return;
		resetCalc(false);
	}

	/** Botão "Limpar Calc" do rodapé — zera grade e campos. */
	async function limparCalc() {
		if (bipagens.value.length === 0 && !pesoStr.value && !qtdCxStr.value) {
			focarPeso();
			return;
		}
		if (bipagens.value.length > 0) {
			const r = await msgDlg(
				"Calc Peso Var",
				"atencao",
				"Limpar o cálculo de peso variável?",
				["Não", "Sim"],
				false,
			);
			if (r !== 1) return;
		}
		resetCalc(true);
	}

	function resetCalc(limparCampos: boolean) {
		bipagens.value = [];
		proxSeq.value = 1;
		if (limparCampos) {
			pesoStr.value = "";
			qtdCxStr.value = "";
		}
		else {
			pesoStr.value = "";
		}
		focarPeso();
	}

	/**
	 * Gerar Lanç — aplica totais no Recebimento e volta para a aba.
	 * Peso variável: total Kg no campo de quantidade/peso; caixas na embalagem.
	 */
	async function gerarLancamento() {
		if (bipagens.value.length === 0) {
			await msgDlg("Calc Peso Var", "atencao", "Não há bipagens para gerar o lançamento.", ["OK"], false);
			return;
		}

		const tab = props.parent.recebimentoTab;
		const kg = totalKg.value;
		const cx = totalCx.value;

		if (tab?.inputQtdCaixa) {
			tab.inputQtdCaixa.inputValue = kg;
		}
		if (tab?.inputEmbalagemPadrao) {
			tab.inputEmbalagemPadrao.inputValue = cx;
		}

		// Store opcional (se existir no pinia) — não quebra se ausente
		const anyStore = store as Record<string, unknown>;
		const setter = anyStore.setCalcPesoVarTotais;
		if (typeof setter === "function") {
			(setter as (v: { kg: number; cx: number; bipagens: number }) => void)({
				kg,
				cx,
				bipagens: bipagens.value.length,
			});
		}

		props.parent.changeActiveTab("recebimento");
		await nextTick();
		tab?.inputQtdCaixa?.setFocus?.();
	}

	onMounted(() => {
		nextTick(() => focarPeso());
	});
</script>

<style scoped>
	.calc-peso-var {
		min-height: 14rem;
		background: #fff;
	}

	.calc-peso-var__label {
		min-width: 4.5rem;
		text-align: right;
		font-size: 0.8rem;
		font-weight: 600;
		color: #333;
	}

	.calc-peso-var__field {
		box-sizing: border-box;
		width: 7.5rem;
		min-height: 1.75rem;
		padding: 0.2rem 0.35rem;
		font-size: 0.85rem;
		border: 1px solid #a0a0a0;
		border-radius: 2px;
		background: #fff;
		color: #212121;
	}

	.calc-peso-var__btn-b {
		flex-shrink: 0;
		width: 1.75rem;
		height: 1.75rem;
		padding: 0;
		font-size: 0.85rem;
		font-weight: 700;
		border: 1px solid #9e9e9e;
		border-radius: 2px;
		background: #f0f0f0;
		color: #212121;
		cursor: pointer;
	}

	.calc-peso-var__btn-b:active {
		background: #d8d8d8;
	}

	.calc-peso-var__totais-wrap {
		border: 1px solid #c8c8c8;
		border-radius: 2px;
		background: #fafafa;
	}

	.calc-peso-var__totais-title {
		padding: 0.15rem 0.45rem;
		font-size: 0.7rem;
		font-weight: 600;
		color: #555;
		border-bottom: 1px solid #e0e0e0;
		background: #f0f0f0;
	}

	.calc-peso-var__totais {
		padding: 0.35rem 0.5rem;
		font-size: 0.9rem;
	}

	.calc-peso-var__total-item {
		font-variant-numeric: tabular-nums;
		color: #212121;
	}

	.calc-peso-var__sep {
		color: #999;
	}

	.calc-peso-var__btn-trash {
		flex-shrink: 0;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 1.75rem;
		height: 1.75rem;
		border: 1px solid #ccc;
		border-radius: 2px;
		background: #fff;
		color: #555;
		cursor: pointer;
	}

	.calc-peso-var__btn-trash:disabled {
		opacity: 0.4;
		cursor: default;
	}

	.calc-peso-var__grid {
		min-height: 10rem;
		border-color: #c8c8c8 !important;
		border-radius: 2px;
	}

	.calc-peso-var__table :deep(.p-datatable-thead > tr > th) {
		font-size: 0.75rem;
		padding: 0.3rem 0.4rem;
		background: #ececec;
		color: #333;
	}

	.calc-peso-var__table :deep(.p-datatable-tbody > tr > td) {
		font-size: 0.8rem;
		padding: 0.25rem 0.4rem;
	}

	.calc-peso-var__table :deep(.p-datatable-emptymessage) {
		text-align: center;
		color: #666;
		font-size: 0.85rem;
		padding: 1.5rem 0.5rem;
	}

	.calc-peso-var__footer {
		padding-top: 0.15rem;
		border-top: 1px solid #e0e0e0;
	}
</style>
